import React, { useCallback, useState } from "react"
import { useDropzone } from "react-dropzone"
import * as XLSX from "xlsx"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  FormGroup,
  Label,
  Input,
  Alert,
} from "reactstrap"
import PrimaryButton from "newcomponents/primary-button"
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"

const requiredHeaders = [
  "FirstName",
  "LastName",
  "Email",
  "Contact",
  "Service",
  "VisitDate",
]

// Display-only labels - requiredHeaders values stay as-is since they're also
// used as the columnMapping keys sent to the backend.
const requiredHeaderLabels = {
  Service: "Engagement",
  VisitDate: "Engagement Date",
}

const UploadModal = ({
  modalOpen,
  toggleModal,
  uploadedFile,
  setUploadedFile,
  handleUpload,
  loading,
  mode = "light",
}) => {
  const [fileHeaders, setFileHeaders] = useState([])
  const [columnMapping, setColumnMapping] = useState({})

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      setUploadedFile(file)

      const reader = new FileReader()
      reader.readAsArrayBuffer(file)
      reader.onload = event => {
        const buffer = event.target.result
        let extractedHeaders = []

        if (file.name.endsWith(".csv")) {
          const text = new TextDecoder("utf-8").decode(buffer)
          const rows = text.split("\n").map(row => row.split(","))
          extractedHeaders = rows[0].map(h => h.trim())
        } else if (file.name.endsWith(".xlsx")) {
          const workbook = XLSX.read(buffer, { type: "array" })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
          extractedHeaders = jsonData[0] || []
        }

        setFileHeaders(extractedHeaders)

        const defaultMapping = {}
        requiredHeaders.forEach(reqHeader => {
          // Match either the internal key (e.g. "Service") or the friendly
          // label shown in the UI (e.g. "Engagement"), since a file's
          // headers may use either.
          const candidates = [reqHeader, requiredHeaderLabels[reqHeader]]
            .filter(Boolean)
            .map(c => c.toLowerCase())
          const foundHeader = extractedHeaders.find(header =>
            candidates.includes(String(header).trim().toLowerCase()),
          )
          if (foundHeader) defaultMapping[reqHeader] = foundHeader
        })

        setColumnMapping(defaultMapping)
      }
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".csv, .xlsx",
    multiple: false,
  })

  const handleHeaderMapping = (requiredField, selectedHeader) => {
    setColumnMapping(prev => ({
      ...prev,
      [requiredField]: selectedHeader,
    }))
  }

  const removeFile = () => {
    setUploadedFile(null)
    setFileHeaders([])
    setColumnMapping({})
  }

  const allRequiredMapped = requiredHeaders.every(field => columnMapping[field])
  const missingHeaders = requiredHeaders.filter(field => !columnMapping[field])

  return (
    <Modal
      isOpen={modalOpen}
      toggle={toggleModal}
      centered
      style={{ maxWidth: "600px" }}
    >
      <div
        style={{
          backgroundColor: mode === "dark" ? "#1f2937" : "#fff",
          borderRadius: "12px",
        }}
      >
        {/* Header */}
        <ModalHeader
          toggle={toggleModal}
          style={{
            backgroundColor: mode === "dark" ? "#1f2937" : "#fff",
            color: mode === "dark" ? "#fff" : "#000",
            fontWeight: "600",
            fontSize: "18px",
            borderBottom:
              mode === "dark" ? "1px solid #374151" : "1px solid #e5e7eb",
            paddingBottom: "16px",
          }}
        >
          Upload Alumni Data
        </ModalHeader>

        {/* Body */}
        <ModalBody
          style={{
            backgroundColor: mode === "dark" ? "#1f2937" : "#fff",
            padding: "30px 40px",
          }}
        >
          {/* File Upload Area */}
          <div className="text-center">
            {uploadedFile ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "14px 20px",
                  borderRadius: "8px",
                  border:
                    mode === "dark" ? "1px solid #374151" : "1px solid #e5e7eb",
                  backgroundColor: mode === "dark" ? "#111827" : "#f9fafb",
                }}
              >
                <span
                  style={{
                    color: mode === "dark" ? "#10b981" : "#047857",
                    fontWeight: "500",
                  }}
                >
                  {uploadedFile.name}
                </span>
                <PrimaryButton
                  onClick={removeFile}
                  style={{
                    background: "transparent",
                    color: "#dc2626",
                    border: "none",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    textTransform: "none",
                  }}
                >
                  <DeleteOutlineIcon style={{ fontSize: "18px" }} /> Remove
                </PrimaryButton>
              </div>
            ) : (
              <div
                {...getRootProps()}
                style={{
                  border: "2px dashed #2990DF",
                  borderRadius: "10px",
                  padding: "40px",
                  textAlign: "center",
                  backgroundColor: isDragActive
                    ? mode === "dark"
                      ? "#111827"
                      : "#f0f9ff"
                    : mode === "dark"
                      ? "#1f2937"
                      : "#f9fafb",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadOutlinedIcon
                  style={{
                    fontSize: "48px",
                    color: "#2990DF",
                    marginBottom: "10px",
                  }}
                />
                <p
                  style={{
                    color: mode === "dark" ? "#9ca3af" : "#6b7280",
                    fontSize: "14px",
                  }}
                >
                  {isDragActive
                    ? "Drop the file here..."
                    : "Drag & drop a file here, or click to select (XLSX)"}
                </p>
              </div>
            )}
          </div>

          {/* Sample Download */}
          {!uploadedFile && (
            <a href="/docs/customersampledoc.xlsx" download>
              <PrimaryButton
                style={{
                  backgroundColor: "#2990DF",
                  color: "#fff",
                  width: "100%",
                  marginTop: "20px",
                  height: "46px",
                  borderRadius: "8px",
                  textTransform: "none",
                }}
              >
                Download Sample File
              </PrimaryButton>
            </a>
          )}

          {/* Missing Header Warning */}
          {fileHeaders.length > 0 && missingHeaders.length > 0 && (
            <Alert color="danger" className="mt-3">
              Missing required headers:{" "}
              {missingHeaders
                .map(header => requiredHeaderLabels[header] || header)
                .join(", ")}
            </Alert>
          )}

          {/* Mapping Section */}
          {uploadedFile && fileHeaders.length > 0 && (
            <div
              style={{
                marginTop: "24px",
                padding: "20px",
                borderRadius: "8px",
                border:
                  mode === "dark" ? "1px solid #374151" : "1px solid #e5e7eb",
              }}
            >
              <h6
                style={{
                  color: "#2990DF",
                  fontWeight: "600",
                  marginBottom: "16px",
                }}
              >
                Map File Headers
              </h6>
              {requiredHeaders.map(requiredField => (
                <FormGroup key={requiredField}>
                  <Label
                    style={{
                      color: mode === "dark" ? "#fff" : "#000",
                      fontSize: "14px",
                    }}
                  >
                    {requiredHeaderLabels[requiredField] || requiredField}
                  </Label>
                  <Input
                    type="select"
                    value={columnMapping[requiredField] || ""}
                    onChange={e =>
                      handleHeaderMapping(requiredField, e.target.value)
                    }
                  >
                    <option value="">Select Column</option>
                    {fileHeaders.map(header => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              ))}
            </div>
          )}
        </ModalBody>

        {/* Footer */}
        <ModalFooter
          style={{
            backgroundColor: mode === "dark" ? "#1f2937" : "#fff",
            justifyContent: "center",
            gap: "12px",
            borderTop:
              mode === "dark" ? "1px solid #374151" : "1px solid #e5e7eb",
            padding: "20px 40px",
          }}
        >
          <PrimaryButton
            onClick={toggleModal}
            style={{
              backgroundColor: "white",
              border:
                mode === "dark" ? "1px solid #374151" : "1px solid #e0e0e0",
              color: "#000",
              fontSize: "14px",
              fontWeight: "500",
              width: "166px",
              height: "46px",
              borderRadius: "5px",
            }}
          >
            Cancel
          </PrimaryButton>

          {uploadedFile && (
            <PrimaryButton
              onClick={() => handleUpload(columnMapping)}
              disabled={!allRequiredMapped || loading}
              style={{
                backgroundColor: "#2990DF",
                color: "#fff",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                width: "166px",
                height: "46px",
                borderRadius: "5px",
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {loading ? <Spinner size="sm" /> : "Upload"}
            </PrimaryButton>
          )}
        </ModalFooter>
      </div>
    </Modal>
  )
}

export default UploadModal
